// TOWN_FLOOR_PLAN, the default office floor plan.
import type { Point2 } from "../proximity";
import {
  TOWN_FLOOR_PLAN_REVISION,
  type Rect,
  type TownArea,
  type TownDecal,
  type TownFloorPlan,
  type TownOpening,
  type TownWallRun,
  type TownWindow,
} from "./types";
import { TOWN_CAR_PARK } from "./car-park";

function rect(x0: number, z0: number, x1: number, z1: number): Rect {
  return { x0, z0, x1, z1 };
}

/**
 * Booth rows, north to south. Each row holds a west and an east booth inside
 * slate walls, with a glass front and a sliding door per booth on its south
 * side. East-west aisles of the hall run between the rows.
 */
const BOOTH_ROWS = [
  { z0: -7.5, z1: -4.5, west: { id: "booth-orchid", name: "Orchid booth" }, east: { id: "booth-bamboo", name: "Bamboo booth" } },
  { z0: -2, z1: 1, west: { id: "booth-maple", name: "Maple booth" }, east: { id: "booth-cedar", name: "Cedar booth" } },
  { z0: 3.5, z1: 6.5, west: { id: "booth-willow", name: "Willow booth" }, east: { id: "booth-fern", name: "Fern booth" } },
] as const;

/** Booth columns: x ranges of the west and east booth in every row. */
const BOOTH_WEST_X = [13, 15.5] as const;
const BOOTH_EAST_X = [16, 18.5] as const;

function boothWalls(): TownWallRun[] {
  return BOOTH_ROWS.flatMap(({ z0, z1, west, east }) => {
    const pair = `the ${west.name} and ${east.name}`;
    return [
      { id: `${west.id}-north`, name: `north wall of ${pair}`, rect: rect(12.5, z0 - 0.5, 19, z0), class: "low" },
      { id: `${west.id}-west`, name: `west wall of the ${west.name}`, rect: rect(12.5, z0, 13, z1), class: "low" },
      { id: `${west.id}-east`, name: `wall between ${pair}`, rect: rect(15.5, z0, 16, z1), class: "low" },
      { id: `${east.id}-east`, name: `east wall of the ${east.name}`, rect: rect(18.5, z0, 19, z1), class: "low" },
      { id: `${west.id}-front`, name: `glass front of ${pair}`, rect: rect(12.5, z1, 19, z1 + 0.5), class: "low-glass" },
    ] satisfies TownWallRun[];
  });
}

function boothDoors(): TownOpening[] {
  return BOOTH_ROWS.flatMap(({ z1, west, east }) => [
    { id: `${west.id}-door`, name: `${west.name} door`, wallId: `${west.id}-front`, rect: rect(14, z1, 15, z1 + 0.5), kind: "sliding-glass", areas: [west.id, "hall"] },
    { id: `${east.id}-door`, name: `${east.name} door`, wallId: `${west.id}-front`, rect: rect(16.5, z1, 17.5, z1 + 0.5), kind: "sliding-glass", areas: [east.id, "hall"] },
  ] satisfies TownOpening[]);
}

function boothAreas(): TownArea[] {
  return BOOTH_ROWS.flatMap(({ z0, z1, west, east }) => [
    { ...west, x: BOOTH_WEST_X },
    { ...east, x: BOOTH_EAST_X },
  ].map(({ id, name, x }): TownArea => ({
    id,
    name,
    kind: "booth",
    rects: [rect(x[0], z0, x[1], z1)],
    acousticZone: id,
    lockable: true,
    exposure: "indoor",
    lightingGroup: "core",
    floor: { pattern: "planks", palette: "booth" },
  })));
}

const walls: readonly TownWallRun[] = [
  // North exterior: tall plaster behind the meeting rooms, a stone parapet behind the terrace.
  { id: "north-wall-west-boardroom", name: "north wall of the West boardroom", rect: rect(-28, -18, -16, -17.5), class: "tall" },
  { id: "north-wall-amber", name: "north wall of the Amber meeting room", rect: rect(-16, -18, -9.5, -17.5), class: "tall" },
  { id: "north-wall-jade", name: "north wall of the Jade meeting room", rect: rect(-9.5, -18, -3, -17.5), class: "tall" },
  { id: "terrace-parapet", name: "terrace parapet", rect: rect(-3, -18, 17, -17.5), class: "railing", finish: { north: "stone", south: "stone" } },
  { id: "north-wall-east-boardroom", name: "north wall of the East boardroom", rect: rect(17, -18, 28, -17.5), class: "tall" },

  // West and east exterior: low walls with windows inset in the cap.
  { id: "west-wall-boardroom", name: "west wall of the West boardroom", rect: rect(-28, -17.5, -27.5, -9.5), class: "low" },
  { id: "west-wall-pantry", name: "west wall of the Pantry", rect: rect(-28, -9.5, -27.5, 7), class: "low" },
  { id: "west-wall-recreation", name: "west wall of the Recreation room", rect: rect(-28, 7, -27.5, 20), class: "low" },
  { id: "east-wall-boardroom", name: "east wall of the East boardroom", rect: rect(27.5, -17.5, 28, -9.5), class: "low" },
  { id: "east-wall-lounges", name: "east wall of the East lounge", rect: rect(27.5, -9.5, 28, 11), class: "low" },
  { id: "east-wall-restrooms", name: "east wall of the restrooms", rect: rect(27.5, 11, 28, 20), class: "low" },

  // South facade: glass on a stone plinth with stone piers, solid stone with frosted windows at the restrooms.
  // Piers repeat from each run's west end, so the glass runs start on even x and their piers line up with the
  // 2.0 mullion module. The recreation run starts on the west wall's corner cell for that reason; the corner
  // still belongs to the west wall, which comes first.
  { id: "south-facade-recreation", name: "south front of the Recreation room", rect: rect(-28, 19.5, -16, 20), class: "low-glass", piers: 4, finish: { south: "stone" } },
  { id: "south-facade-west-lounge", name: "south front of the West lounge", rect: rect(-16, 19.5, -6, 20), class: "low-glass", piers: 4, finish: { south: "stone" } },
  { id: "south-facade-round-lounge", name: "south front of the Round lounge", rect: rect(6, 19.5, 16, 20), class: "low-glass", piers: 4, finish: { south: "stone" } },
  { id: "south-facade-restrooms", name: "south wall of the restrooms", rect: rect(16, 19.5, 27.5, 20), class: "low", frosted: true, finish: { south: "stone" } },

  // Entrance recess and storefront.
  { id: "entrance-west-wall", name: "west wall of the entrance", rect: rect(-6.5, 11, -6, 19.5), class: "low" },
  { id: "entrance-east-wall", name: "east wall of the entrance", rect: rect(6, 11, 6.5, 19.5), class: "low" },
  { id: "storefront", name: "entrance storefront", rect: rect(-6, 17, 6, 17.5), class: "low-glass" },

  // North row fronts, with the terrace planters between them.
  { id: "west-boardroom-front", name: "front wall of the West boardroom", rect: rect(-27.5, -10, -16, -9.5), class: "low" },
  { id: "amber-front", name: "glass front of the Amber meeting room", rect: rect(-16, -10, -9.5, -9.5), class: "low-glass" },
  { id: "jade-front", name: "glass front of the Jade meeting room", rect: rect(-9.5, -10, -3, -9.5), class: "low-glass" },
  { id: "terrace-planters", name: "terrace planters", rect: rect(-3, -10, 17, -9.5), class: "planter" },
  { id: "east-boardroom-front", name: "front wall of the East boardroom", rect: rect(17, -10, 27.5, -9.5), class: "low" },

  // North row partitions.
  { id: "west-boardroom-amber-wall", name: "wall between the West boardroom and the Amber meeting room", rect: rect(-16.5, -17.5, -16, -10), class: "low" },
  { id: "amber-jade-wall", name: "wall between the Amber and Jade meeting rooms", rect: rect(-10, -17.5, -9.5, -10), class: "low" },
  { id: "jade-terrace-wall", name: "wall between the Jade meeting room and the terrace", rect: rect(-3.5, -17.5, -3, -10), class: "low" },
  { id: "terrace-east-boardroom-wall", name: "wall between the terrace and the East boardroom", rect: rect(17, -17.5, 17.5, -10), class: "low" },

  // West column.
  { id: "pantry-glass", name: "glass wall of the Pantry", rect: rect(-16.5, -9.5, -16, 7), class: "low-glass" },
  { id: "recreation-east-wall", name: "east wall of the Recreation room", rect: rect(-16.5, 7, -16, 19.5), class: "low" },

  // Booth block.
  ...boothWalls(),

  // South row fronts.
  { id: "west-lounge-railing", name: "railing of the West lounge", rect: rect(-16, 11, -6.5, 11.5), class: "railing" },
  { id: "round-lounge-railing", name: "railing of the Round lounge", rect: rect(6.5, 11, 15.5, 11.5), class: "railing" },
  { id: "round-lounge-east-wall", name: "wall between the Round lounge and the restrooms", rect: rect(15.5, 11, 16, 19.5), class: "low" },
  { id: "restroom-front", name: "front wall of the restrooms", rect: rect(16, 11, 27.5, 11.5), class: "low" },
  { id: "restroom-divider", name: "wall between the restrooms", rect: rect(21.5, 11.5, 22, 19.5), class: "low" },
  { id: "restroom-west-stall-wall", name: "stall wall of the West restroom", rect: rect(16, 14.5, 21.5, 15), class: "low", finish: { south: "tile" } },
  { id: "restroom-east-stall-wall", name: "stall wall of the East restroom", rect: rect(22, 14.5, 27.5, 15), class: "low", finish: { south: "tile" } },

  // Garden hedges. The north hedge blocks the three node rows the tall wall's face and cap hide, so nobody walks
  // there. The south hedges line the facade.
  { id: "north-hedge", name: "north garden hedge", rect: rect(-28, -19, 28, -18.5), class: "hedge" },
  { id: "north-hedge-outer", name: "outer north garden hedge", rect: rect(-28, -19.5, 28, -19), class: "hedge" },
  { id: "south-west-hedge", name: "south-west garden hedge", rect: rect(-27.5, 20.5, -7, 21), class: "hedge" },
  { id: "south-east-hedge", name: "south-east garden hedge", rect: rect(7, 20.5, 27.5, 21), class: "hedge" },

  // Planters either side of the entrance recess, on the facade line.
  { id: "entrance-west-planter", name: "west entrance planter", rect: rect(-6, 19.5, -3.5, 20), class: "planter" },
  { id: "entrance-east-planter", name: "east entrance planter", rect: rect(3.5, 19.5, 6, 20), class: "planter" },
];

const openings: readonly TownOpening[] = [
  { id: "west-boardroom-door", name: "West boardroom door", wallId: "west-boardroom-front", rect: rect(-20.5, -10, -18, -9.5), kind: "sliding-glass", areas: ["west-boardroom", "pantry"] },
  { id: "amber-door", name: "Amber meeting room door", wallId: "amber-front", rect: rect(-14, -10, -12, -9.5), kind: "sliding-glass", areas: ["amber-room", "hall"] },
  { id: "jade-door", name: "Jade meeting room door", wallId: "jade-front", rect: rect(-7.5, -10, -5.5, -9.5), kind: "sliding-glass", areas: ["jade-room", "hall"] },
  { id: "terrace-west-gate", name: "west terrace gate", wallId: "terrace-planters", rect: rect(-2.5, -10, 0, -9.5), kind: "gate", areas: ["terrace", "hall"] },
  { id: "terrace-east-gate", name: "east terrace gate", wallId: "terrace-planters", rect: rect(10, -10, 12.5, -9.5), kind: "gate", areas: ["terrace", "hall"] },
  { id: "east-boardroom-door", name: "East boardroom door", wallId: "east-boardroom-front", rect: rect(19, -10, 21.5, -9.5), kind: "sliding-glass", areas: ["east-boardroom", "east-lounge"] },
  { id: "west-garden-door", name: "west garden door", wallId: "west-wall-pantry", rect: rect(-28, -3, -27.5, 0), kind: "sliding-glass", areas: ["pantry", "garden"] },
  { id: "east-garden-door", name: "east garden door", wallId: "east-wall-lounges", rect: rect(27.5, -3, 28, 0), kind: "sliding-glass", areas: ["east-lounge", "garden"] },
  { id: "pantry-north-arch", name: "north arch of the Pantry", wallId: "pantry-glass", rect: rect(-16.5, -6.5, -16, -4.5), kind: "arch", areas: ["pantry", "hall"] },
  { id: "pantry-south-arch", name: "south arch of the Pantry", wallId: "pantry-glass", rect: rect(-16.5, 2, -16, 4), kind: "arch", areas: ["pantry", "hall"] },
  { id: "recreation-arch", name: "Recreation room arch", wallId: "recreation-east-wall", rect: rect(-16.5, 7.5, -16, 9.5), kind: "arch", areas: ["recreation", "hall"] },
  ...boothDoors(),
  { id: "west-lounge-gate", name: "West lounge gate", wallId: "west-lounge-railing", rect: rect(-9.5, 11, -7.5, 11.5), kind: "gate", areas: ["west-lounge", "hall"] },
  { id: "round-lounge-gate", name: "Round lounge gate", wallId: "round-lounge-railing", rect: rect(7.5, 11, 9.5, 11.5), kind: "gate", areas: ["round-lounge", "hall"] },
  { id: "restroom-west-door", name: "West restroom door", wallId: "restroom-front", rect: rect(17.5, 11, 19, 11.5), kind: "sliding-opaque", areas: ["restroom-west", "hall"] },
  { id: "restroom-east-door", name: "East restroom door", wallId: "restroom-front", rect: rect(24, 11, 25.5, 11.5), kind: "sliding-opaque", areas: ["restroom-east", "east-nook"] },
  { id: "restroom-west-stall-door", name: "West restroom stall door", wallId: "restroom-west-stall-wall", rect: rect(19.5, 14.5, 20.5, 15), kind: "sliding-opaque", areas: ["restroom-west-stall", "restroom-west"] },
  { id: "restroom-east-stall-door", name: "East restroom stall door", wallId: "restroom-east-stall-wall", rect: rect(25.5, 14.5, 26.5, 15), kind: "sliding-opaque", areas: ["restroom-east-stall", "restroom-east"] },
  { id: "main-door", name: "main entrance", wallId: "storefront", rect: rect(-1.5, 17, 1.5, 17.5), kind: "sliding-glass", areas: ["reception", "plaza"] },
];

/** Tall windows start and end on even x, so the 2.0 mullion module gives whole panes. */
const windows: readonly TownWindow[] = [
  { id: "west-boardroom-north-window", wallId: "north-wall-west-boardroom", rect: rect(-26, -18, -18, -17.5) },
  { id: "amber-north-window", wallId: "north-wall-amber", rect: rect(-16, -18, -10, -17.5) },
  { id: "jade-north-window", wallId: "north-wall-jade", rect: rect(-8, -18, -4, -17.5) },
  { id: "east-boardroom-north-window", wallId: "north-wall-east-boardroom", rect: rect(18, -18, 26, -17.5) },
  { id: "west-boardroom-side-window", wallId: "west-wall-boardroom", rect: rect(-28, -16, -27.5, -11.5) },
  { id: "pantry-north-window", wallId: "west-wall-pantry", rect: rect(-28, -8, -27.5, -5) },
  { id: "pantry-south-window", wallId: "west-wall-pantry", rect: rect(-28, 2, -27.5, 5.5) },
  { id: "recreation-north-window", wallId: "west-wall-recreation", rect: rect(-28, 9, -27.5, 12.5) },
  { id: "recreation-south-window", wallId: "west-wall-recreation", rect: rect(-28, 14.5, -27.5, 18) },
  { id: "east-boardroom-side-window", wallId: "east-wall-boardroom", rect: rect(27.5, -16, 28, -11.5) },
  { id: "east-lounge-window", wallId: "east-wall-lounges", rect: rect(27.5, -8, 28, -5) },
  { id: "east-nook-window", wallId: "east-wall-lounges", rect: rect(27.5, 3, 28, 9.5) },
];

/**
 * The street's kerb, a zebra crossing in front of the entrance walk, the driveway into the car park and the footpath
 * from the car park to the walk. Centre dashes and bay lines are thinner than the lattice, so the renderers paint them.
 */
function streetDecals(): TownDecal[] {
  const { street, driveway, footpath } = TOWN_CAR_PARK;
  const zebra = [-2.5, -1.5, -.5, .5, 1.5].map((x, index): TownDecal => ({
    id: `street-zebra-${index}`, shape: "rect", rect: rect(x, street.z0 + .5, x + .5, street.z1 - .5), pattern: "carpet", palette: "paint",
  }));
  return [
    { id: "street", shape: "rect", rect: street, pattern: "carpet", palette: "street", border: "kerb" },
    { id: "car-park-driveway", shape: "rect", rect: driveway, pattern: "carpet", palette: "street" },
    { id: "car-park-footpath", shape: "rect", rect: footpath, pattern: "pavers", palette: "path", border: "kerb" },
    ...zebra,
  ];
}

const decals: readonly TownDecal[] = [
  // Walks. The south walk stops at the plaza, whose paving already joins its two halves.
  { id: "south-walk-west", shape: "rect", rect: rect(-28, 21.5, -6, 22.5), pattern: "slabs", palette: "plaza", border: "kerb" },
  { id: "south-walk-east", shape: "rect", rect: rect(6, 21.5, 28, 22.5), pattern: "slabs", palette: "plaza", border: "kerb" },
  { id: "entrance-walk", shape: "rect", rect: rect(-2.5, 22.5, 2.5, 32), pattern: "pavers", palette: "path", border: "kerb" },
  { id: "west-garden-path", shape: "rect", rect: rect(-33, -3, -28, 0), pattern: "slabs", palette: "plaza", border: "kerb" },
  { id: "east-garden-path", shape: "rect", rect: rect(28, -3, 33, 0), pattern: "slabs", palette: "plaza", border: "kerb" },
  { id: "entrance-mat", shape: "rect", rect: rect(-1.5, 18, 1.5, 19.5), pattern: "mat", palette: "green", border: "mat" },
  // Planting beds.
  { id: "north-bed", shape: "rect", rect: rect(-28, -19.5, 28, -18), pattern: "bed", palette: "soil" },
  // Terrace: a paved strip and bed on the east side, and paving through both planter gates.
  { id: "terrace-path", shape: "rect", rect: rect(13.5, -17.5, 17, -10), pattern: "pavers", palette: "path" },
  { id: "terrace-bed", shape: "rect", rect: rect(15, -14.5, 17, -10.5), pattern: "bed", palette: "soil" },
  { id: "terrace-west-gate-paving", shape: "rect", rect: rect(-2.5, -10.5, 0, -9.5), pattern: "pavers", palette: "path" },
  { id: "terrace-east-gate-paving", shape: "rect", rect: rect(10, -10.5, 12.5, -9.5), pattern: "pavers", palette: "path" },
  // Booth aisles.
  { id: "booth-north-aisle", shape: "rect", rect: rect(12.5, -4, 19, -2.5), pattern: "carpet", palette: "aisle" },
  { id: "booth-south-aisle", shape: "rect", rect: rect(12.5, 1.5, 19, 3), pattern: "carpet", palette: "aisle" },
  // Rugs.
  { id: "recreation-rug", shape: "rect", rect: rect(-22.5, 9, -17.5, 14.5), pattern: "rug", palette: "gold", border: "gold" },
  { id: "east-lounge-rug", shape: "rect", rect: rect(20.5, -8.5, 26, -2.5), pattern: "rug", palette: "beige", border: "beige" },
  { id: "west-lounge-rug", shape: "rect", rect: rect(-13.5, 13, -8.5, 18.5), pattern: "rug", palette: "sage", border: "sage" },
  { id: "round-lounge-rug", shape: "ellipse", rect: rect(8, 12.5, 14, 18.5), pattern: "rug", palette: "sky", border: "sky" },
  { id: "east-nook-rug", shape: "rect", rect: rect(20.5, 2, 27, 9.5), pattern: "carpet", palette: "blue", border: "sky" },
  ...streetDecals(),
];

const indoor = { exposure: "indoor", acousticZone: null, lockable: false } as const;
const outdoor = { exposure: "outdoor", acousticZone: "garden", lockable: false } as const;

function meetingRoom(id: string, name: string, bounds: Rect, palette: "blue" | "grey", detail: string): TownArea {
  const x = (bounds.x0 + bounds.x1) / 2;
  return {
    id,
    name,
    kind: "meeting-room",
    rects: [bounds],
    acousticZone: id,
    lockable: true,
    exposure: "indoor",
    lightingGroup: "perimeter",
    floor: { pattern: "carpet", palette },
    label: { anchor: { x, z: -16.5 }, tone: "blue", detail },
    activitySlots: [{ id: `${id}-display`, kind: "display", anchor: { x, z: -17.5 }, reach: "area" }],
  };
}

const areas: readonly TownArea[] = [
  meetingRoom("west-boardroom", "West boardroom", rect(-27.5, -17.5, -16.5, -10), "blue", "10 seats"),
  meetingRoom("amber-room", "Amber meeting room", rect(-16, -17.5, -10, -10), "blue", "4 seats"),
  meetingRoom("jade-room", "Jade meeting room", rect(-9.5, -17.5, -3.5, -10), "grey", "4 seats"),
  meetingRoom("east-boardroom", "East boardroom", rect(17.5, -17.5, 27.5, -10), "blue", "8 seats"),
  {
    id: "terrace", name: "Garden terrace", kind: "terrace", rects: [rect(-3, -17.5, 17, -10)],
    ...outdoor, lightingGroup: "terrace", floor: { pattern: "deck", palette: "teak" },
    label: { anchor: { x: 5, z: -16.5 }, tone: "sage", detail: "Tables + fresh air" },
    activitySlots: [{ id: "terrace-music", kind: "music", anchor: { x: 5, z: -14 }, reach: { radius: 5 } }],
  },
  {
    id: "pantry", name: "Pantry", kind: "pantry", rects: [rect(-27.5, -9.5, -16.5, 7)],
    ...indoor, lightingGroup: "perimeter", floor: { pattern: "checker", palette: "cream" },
    label: { anchor: { x: -22, z: -8.5 }, tone: "sage", detail: "Coffee + lunch" },
  },
  {
    id: "recreation", name: "Recreation room", kind: "recreation", rects: [rect(-27.5, 7, -16.5, 19.5)],
    ...indoor, lightingGroup: "perimeter", floor: { pattern: "carpet", palette: "recreation" },
    label: { anchor: { x: -22, z: 8 }, tone: "gold", detail: "Games + lounge" },
    activitySlots: [{ id: "recreation-games", kind: "game", anchor: { x: -24.5, z: 11.5 }, reach: "area" }],
  },
  {
    id: "open-office", name: "Open studio", kind: "open-office", rects: [rect(-14, -7.5, 10, 9.5)],
    ...indoor, lightingGroup: "core", floor: { pattern: "carpet", palette: "grey" },
    label: { anchor: { x: -2, z: -7 }, tone: "blue", detail: "Team desks" },
  },
  {
    id: "hall", name: "Hall", kind: "corridor",
    rects: [
      rect(-16, -9.5, 19, -8),
      rect(-16, -8, 12.5, -7.5),
      rect(-16, -7.5, -14, 11),
      rect(10, -7.5, 12.5, 11),
      rect(-14, 9.5, 10, 11),
      rect(12.5, -4, 19, -2.5),
      rect(12.5, 1.5, 19, 3),
      rect(12.5, 7, 19, 11),
    ],
    ...indoor, lightingGroup: "core", floor: { pattern: "planks", palette: "hall" },
  },
  ...boothAreas(),
  {
    id: "east-lounge", name: "East lounge", kind: "lounge", rects: [rect(19, -9.5, 27.5, 0)],
    ...indoor, lightingGroup: "perimeter", floor: { pattern: "planks", palette: "hall" },
    label: { anchor: { x: 23.5, z: -9 }, tone: "sage", detail: "Sofas + books" },
  },
  {
    id: "east-nook", name: "East nook", kind: "lounge", rects: [rect(19, 0, 27.5, 11)],
    ...indoor, lightingGroup: "perimeter", floor: { pattern: "planks", palette: "hall" },
  },
  {
    id: "west-lounge", name: "West lounge", kind: "lounge", rects: [rect(-16, 11.5, -6.5, 19.5)],
    ...indoor, lightingGroup: "perimeter", floor: { pattern: "planks", palette: "hall" },
  },
  {
    id: "reception", name: "Reception", kind: "reception", rects: [rect(-6, 11, 6, 17)],
    ...indoor, lightingGroup: "perimeter", floor: { pattern: "planks", palette: "hall" },
    label: { anchor: { x: 0, z: 15 }, tone: "blue", detail: "Welcome" },
  },
  {
    id: "round-lounge", name: "Round lounge", kind: "lounge", rects: [rect(6.5, 11.5, 15.5, 19.5)],
    ...indoor, lightingGroup: "perimeter", floor: { pattern: "planks", palette: "hall" },
  },
  {
    id: "restroom-west", name: "West restroom", kind: "restroom", rects: [rect(16, 11.5, 21.5, 14.5)],
    ...indoor, acousticZone: "restroom-west", lightingGroup: "perimeter", floor: { pattern: "tile", palette: "restroom" },
  },
  {
    id: "restroom-west-stall", name: "West restroom stall", kind: "restroom", rects: [rect(16, 15, 21.5, 19.5)],
    ...indoor, acousticZone: "restroom-west", lightingGroup: "perimeter", floor: { pattern: "tile", palette: "restroom" },
  },
  {
    id: "restroom-east", name: "East restroom", kind: "restroom", rects: [rect(22, 11.5, 27.5, 14.5)],
    ...indoor, acousticZone: "restroom-east", lightingGroup: "perimeter", floor: { pattern: "tile", palette: "restroom" },
  },
  {
    id: "restroom-east-stall", name: "East restroom stall", kind: "restroom", rects: [rect(22, 15, 27.5, 19.5)],
    ...indoor, acousticZone: "restroom-east", lightingGroup: "perimeter", floor: { pattern: "tile", palette: "restroom" },
  },
  {
    id: "plaza", name: "Entrance plaza", kind: "plaza", rects: [rect(-6, 17.5, 6, 27), rect(-3, 27, 3, 32)],
    ...outdoor, lightingGroup: "exterior", floor: { pattern: "slabs", palette: "plaza" },
  },
  {
    id: "garden", name: "Garden", kind: "garden",
    rects: [
      rect(-33, -23, 33, -18),
      rect(-33, -18, -28, 20),
      rect(28, -18, 33, 20),
      rect(-33, 20, -6, 27),
      rect(6, 20, 33, 27),
      rect(-33, 27, -31, 32),
      rect(-7, 27, -3, 32),
      rect(3, 27, 33, 32),
    ],
    ...outdoor, lightingGroup: "exterior", floor: { pattern: "grass", palette: "meadow" },
  },
  {
    id: "car-park", name: "Car park", kind: "plaza", rects: [TOWN_CAR_PARK.lot],
    ...outdoor, lightingGroup: "exterior", floor: { pattern: "carpet", palette: "street" },
  },
  {
    id: "sidewalk", name: "Sidewalk", kind: "plaza", rects: [TOWN_CAR_PARK.sidewalk],
    ...outdoor, lightingGroup: "exterior", floor: { pattern: "slabs", palette: "plaza" },
  },
  {
    id: "street", name: "Street", kind: "plaza", rects: [TOWN_CAR_PARK.street],
    ...outdoor, lightingGroup: "exterior", floor: { pattern: "carpet", palette: "street" },
  },
];

/** Arrival columns fan out from the walk's centre line, nearest the entrance first. */
const ARRIVAL_COLUMNS = [0, -1, 1, -2, 2, -3, 3] as const;
const ARRIVAL_ROWS = [21, 22, 23, 24, 25, 26] as const;
const arrivalSlots: readonly Point2[] = ARRIVAL_ROWS.flatMap((z) => ARRIVAL_COLUMNS.map((x) => ({ x, z })));

/**
 * The default office, revision 3. A 56 x 38 building on a 66 x 60 campus:
 * meeting rooms and a terrace along the tall north wall, the pantry and
 * recreation room on the west, open desks in the middle, six booths and two
 * lounges on the east, and reception behind a recessed glass entrance on the
 * south facade, with gardens, hedges and walks all round. A car park in the
 * south-west corner and a street along the south edge are where people
 * arrive.
 */
export const TOWN_FLOOR_PLAN: TownFloorPlan = {
  id: "town-office",
  revision: TOWN_FLOOR_PLAN_REVISION,
  campus: rect(-33, -23, 33, 37),
  building: rect(-28, -18, 28, 20),
  walls,
  openings,
  windows,
  decals,
  areas,
  spawn: { x: 0, z: 23 },
  arrivalSlots,
  accessPoints: [
    { x: 0, z: 21 },
    { x: -31, z: -1.5 },
    { x: 31, z: -1.5 },
    { x: 5, z: -11 },
    { x: -18, z: -21.5 },
  ],
};
